import { onAuthStateChanged, signInWithEmailAndPassword, signOut, type User } from "firebase/auth";
import { doc, getDoc, Timestamp } from "firebase/firestore";
import logger from "@/logger";
import { authService, db } from "./firebase";

export interface UserCredential {
    user: AuthUser;
}

export type AuthUser = {
    displayName: string | null;
    email: string | null;
    uid: string;
    adminAccessGrantedAt: Date | null;
};

/**
 * Convert Firestore adminAccessGrantedAt field to Date
 */
function convertAdminAccessGrantedAt(userData: unknown): Date | null {
    if (!userData || typeof userData !== "object") {
        return null;
    }
    const data = userData as { adminAccessGrantedAt?: unknown };
    const adminAccess = data.adminAccessGrantedAt;

    if (adminAccess instanceof Timestamp) {
        return adminAccess.toDate();
    }
    if (adminAccess) {
        return new Date(adminAccess as string | number | Date);
    }
    return null;
}

/**
 * Create AuthUser from Firebase User and admin access date
 */
function createAuthUser(firebaseUser: User, adminAccessGrantedAt: Date | null): AuthUser {
    return {
        adminAccessGrantedAt,
        displayName: firebaseUser.displayName,
        email: firebaseUser.email,
        uid: firebaseUser.uid,
    };
}

/**
 * Get admin access for a user from Firestore
 */
async function getAdminAccessForUser(uid: string): Promise<Date | null> {
    try {
        const userDoc = await getDoc(doc(db, "users", uid));
        const userData = userDoc.data();
        return convertAdminAccessGrantedAt(userData);
    } catch (error) {
        logger.error("Error checking admin status:", error);
        return null;
    }
}

const firebaseAuthService = {
    /**
     * Get current authenticated user
     */
    getCurrentAuthUser: (): AuthUser | null => {
        const currentUser = authService.currentUser;
        if (!currentUser) {
            return null;
        }

        // Note: adminAccessGrantedAt will be checked separately via Firestore query
        return {
            adminAccessGrantedAt: null, // Will be updated after Firestore check
            displayName: currentUser.displayName,
            email: currentUser.email,
            uid: currentUser.uid,
        };
    },

    /**
     * Subscribe to auth state changes
     */
    onAuthStateChanged: (callback: (user: AuthUser | null) => void): (() => void) => {
        return onAuthStateChanged(authService, async (firebaseUser: User | null) => {
            if (!firebaseUser) {
                callback(null);
                return;
            }

            const adminAccessGrantedAt = await getAdminAccessForUser(firebaseUser.uid);
            callback(createAuthUser(firebaseUser, adminAccessGrantedAt));
        });
    },

    /**
     * Sign in with email and password
     * Includes admin check - if user is not admin, signs them out and throws error
     */
    signInWithEmailAndPassword: async (email: string, password: string): Promise<UserCredential> => {
        try {
            logger.info("Signing in with email and password", { email });

            // Sign in with Firebase Auth
            const userCredential = await signInWithEmailAndPassword(authService, email, password);
            const firebaseUser = userCredential.user;

            logger.info("Firebase Auth sign-in successful", { uid: firebaseUser.uid });

            // Check if user is admin by querying Firestore
            const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));

            if (!userDoc.exists()) {
                logger.warn("User document not found in Firestore", { uid: firebaseUser.uid });
                await signOut(authService);
                throw new Error("User not found");
            }

            const userData = userDoc.data();
            const adminAccessGrantedAt = convertAdminAccessGrantedAt(userData);

            if (!adminAccessGrantedAt) {
                logger.warn("User is not an admin", { email, uid: firebaseUser.uid });
                await signOut(authService);
                throw new Error("Access denied. Admin access required.");
            }

            logger.info("Admin check passed", { adminAccessGrantedAt, email, uid: firebaseUser.uid });

            return {
                user: createAuthUser(firebaseUser, adminAccessGrantedAt),
            };
        } catch (error) {
            logger.error("Error signing in:", error);
            throw error;
        }
    },

    /**
     * Sign out current user
     */
    signOut: async (): Promise<void> => {
        try {
            await signOut(authService);
            logger.info("User signed out");
        } catch (error) {
            logger.error("Error signing out:", error);
            throw error;
        }
    },
};

export default firebaseAuthService;
